import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";

import { Button } from "../../components/base/Button";
import { Toggle } from "../../components/base/Toggle";
import LoadingWheel from "../../components/shared/LoadingWheel";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import AdditionalInstructionsPanel from "../../components/TitleConfiguration/AdditionalInstructionsPanel";
import AspectPriorityList from "../../components/TitleConfiguration/AspectPriorityList";
import BasicSettings from "../../components/TitleConfiguration/BasicSettings";
import CategoryList from "../../components/TitleConfiguration/CategoryList";
import CategoryPickerModal from "../../components/TitleConfiguration/CategoryPickerModal";
import DamageDescriptionPreview from "../../components/TitleConfiguration/DamageDescriptionPreview";
import DamagePromptPanel from "../../components/TitleConfiguration/DamagePromptPanel";
import FieldPriorityList from "../../components/TitleConfiguration/FieldPriorityList";
import LiveTitlePreview from "../../components/TitleConfiguration/LiveTitlePreview";
import TitleBuildingRules from "../../components/TitleConfiguration/TitleBuildingRules";

import {
  useTitleConfig,
  useTitleConfigCategories,
} from "../../data/TitleConfig/useTitleConfig";
import useTitleConfigMutations from "../../data/TitleConfig/useTitleConfigMutations";
import {
  DEFAULT_EXAMPLE_DATA,
  IDamagePreviewResult,
  ITitleConfigCategorySummary,
  ITitleConfigRequest,
  ITitleConfigResponse,
  ITitlePreviewExampleData,
  ITitlePreviewResult,
  TitleType,
} from "../../models/Category/TitleConfig";

/** Strips the response-only fields so what we send back matches TitleConfigRequest. */
function toRequest(response: ITitleConfigResponse): ITitleConfigRequest {
  const {
    id,
    partCategoryId,
    categoryName,
    compiledSystemPrompt,
    compiledDamagePrompt,
    isSaved,
    ...request
  } = response;

  return request;
}

export default function TitleConfigurationPage() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState<ITitleConfigRequest | null>(null);
  const [exampleData, setExampleData] = useState<ITitlePreviewExampleData>(
    DEFAULT_EXAMPLE_DATA,
  );
  const [titlePreview, setTitlePreview] = useState<ITitlePreviewResult>();
  const [damagePreview, setDamagePreview] = useState<IDamagePreviewResult>();
  const [addOpen, setAddOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] =
    useState<ITitleConfigCategorySummary>();

  // The picker lists only categories that have been added; everything else is reachable through
  // the Add and Duplicate modals, which query the unconfigured and full lists themselves.
  const { categories } = useTitleConfigCategories({
    searchTerm,
    configured: true,
  });
  const { config } = useTitleConfig(categoryId);
  const {
    create,
    remove,
    save,
    resetToDefault,
    duplicate,
    recompileAll,
    previewTitle,
    previewDamage,
  } = useTitleConfigMutations();

  const configured = useMemo(() => categories.data ?? [], [categories.data]);

  useEffect(() => {
    setInfo({
      name: "Title Configuration",
      desc: "Configure how listing titles and condition descriptions are built for each part category.",
      pageTitle: "Title Configuration",
    });
  }, [setInfo]);

  // Land on the first configured category, and step away from one that has just been removed.
  useEffect(() => {
    // On a failed load the list is empty but unknown, not empty-and-known - navigating away
    // from the selected category on that basis would just hide what the user was working on.
    if (categories.isLoading || categories.isError || searchTerm) return;

    const stillExists =
      !!categoryId &&
      configured.some((c) => c.partCategoryId === categoryId);

    if (stillExists) return;

    if (configured.length > 0) {
      navigate(`/title-configuration/${configured[0].partCategoryId}`, {
        replace: true,
      });
    } else if (categoryId) {
      navigate("/title-configuration", { replace: true });
    }
  }, [
    categoryId,
    configured,
    categories.isLoading,
    categories.isError,
    searchTerm,
    navigate,
  ]);

  // Reload the form whenever the server hands us a different category's config.
  useEffect(() => {
    if (!config.data) return;

    setForm(toRequest(config.data));
    setTitlePreview(undefined);
    setDamagePreview(undefined);
  }, [config.data]);

  const categoryName = config.data?.categoryName ?? "";

  const exampleForPreview = useMemo(
    () => ({
      ...exampleData,
      categoryName: exampleData.categoryName || categoryName,
    }),
    [exampleData, categoryName],
  );

  const patchForm = (patch: Partial<ITitleConfigRequest>) =>
    setForm((current) => (current ? { ...current, ...patch } : current));

  const handleTitleTypeChange = async (titleType: TitleType) => {
    if (!form || !categoryId || form.titleType === titleType) return;

    // Take the new type's default priority and removal lists, but keep everything the user typed.
    const defaults = await resetToDefault.mutateAsync({
      partCategoryId: categoryId,
      titleType,
    });

    patchForm({
      titleType,
      fieldPriorities: defaults.data.fieldPriorities,
      removalOrder: defaults.data.removalOrder,
    });
  };

  const handleResetToDefault = async () => {
    if (!form || !categoryId) return;

    const defaults = await resetToDefault.mutateAsync({
      partCategoryId: categoryId,
      titleType: form.titleType,
    });

    setForm(toRequest(defaults.data));
  };

  const handleSave = () => {
    if (!form || !categoryId) return;

    save.mutate({ partCategoryId: categoryId, config: form });
  };

  const handleAdd = async (partCategoryIds: string[]) => {
    const result = await create.mutateAsync(partCategoryIds);

    setAddOpen(false);

    // Jump straight to the first one added so it can be configured immediately.
    if (result.data?.length) {
      setSearchTerm("");
      navigate(`/title-configuration/${result.data[0].partCategoryId}`);
    }
  };

  const handleRemove = async () => {
    if (!pendingRemoval) return;

    await remove.mutateAsync(pendingRemoval.partCategoryId);
    setPendingRemoval(undefined);
  };

  const handlePreviewTitle = async () => {
    if (!form || !categoryId) return;

    const result = await previewTitle.mutateAsync({
      partCategoryId: categoryId,
      config: form,
      exampleData: exampleForPreview,
    });

    setTitlePreview(result.data);
  };

  const handlePreviewDamage = async () => {
    if (!form || !categoryId) return;

    const result = await previewDamage.mutateAsync({
      partCategoryId: categoryId,
      config: form,
    });

    setDamagePreview(result.data);
  };

  const handleDuplicate = async (targetPartCategoryIds: string[]) => {
    if (!categoryId) return;

    await duplicate.mutateAsync({
      sourcePartCategoryId: categoryId,
      targetPartCategoryIds,
    });
    setDuplicateOpen(false);
  };

  const showEditor = !!categoryId && !config.isLoading && !!form;

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="xl:col-span-3">
          <CategoryList
            categories={configured}
            selectedId={categoryId}
            searchTerm={searchTerm}
            isLoading={categories.isLoading}
            error={(categories.error as Error) ?? null}
            onSearchChange={setSearchTerm}
            onSelect={(id) => navigate(`/title-configuration/${id}`)}
            onAdd={() => setAddOpen(true)}
            onRemove={setPendingRemoval}
            onRebuildAll={() => recompileAll.mutate()}
            isRebuilding={recompileAll.isPending}
          />
        </div>

        {categories.isError ? (
          <div className="xl:col-span-9 rounded border border-red-200 bg-red-50 p-6">
            <p className="text-sm font-medium text-red-800">
              Could not load title configurations
            </p>
            <p className="mt-1 text-sm text-red-700">
              {(categories.error as Error)?.message}
            </p>
            <p className="mt-3 text-xs text-red-700">
              A 404 usually means the API has not been restarted since this
              feature was added. A 500 usually means the
              PartCategoryTitleConfigs table is missing — run the
              AddedPartCategoryTitleConfig migration against the admin database.
            </p>
          </div>
        ) : !categoryId && !categories.isLoading ? (
          <div className="xl:col-span-9 flex flex-col items-center justify-center rounded border border-dashed border-gray-300 bg-white py-24 text-center">
            <p className="text-sm text-gray-600">
              No category selected.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Add a category to give it its own title rules and damage prompt.
            </p>
            <Button className="mt-4" onClick={() => setAddOpen(true)}>
              Add a category
            </Button>
          </div>
        ) : !showEditor ? (
          <div className="xl:col-span-9 flex items-center justify-center py-24">
            <LoadingWheel />
          </div>
        ) : (
          <div className="xl:col-span-9 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold">{categoryName}</h2>
                <span
                  className={clsx(
                    "rounded px-2 py-0.5 text-xs",
                    form.titleType === "Mechanical"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-green-100 text-green-700",
                  )}
                >
                  {form.titleType}
                </span>
                {!config.data?.isSaved && (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    Not added yet — showing defaults
                  </span>
                )}
                <Toggle
                  labelRight
                  label="Active"
                  checked={form.isActive}
                  onChange={(value) => patchForm({ isActive: value })}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  onClick={() => setDuplicateOpen(true)}
                  disabled={!config.data?.isSaved}
                >
                  Duplicate
                </Button>
                <Button onClick={handleSave} disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </div>

            {!form.isActive && (
              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                This configuration is inactive. Parts in this category will use
                the built-in title rules until it is turned back on.
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 2xl:grid-cols-3">
              <div className="space-y-4">
                <BasicSettings
                  config={form}
                  onChange={patchForm}
                  onTitleTypeChange={handleTitleTypeChange}
                />
                <FieldPriorityList
                  titleType={form.titleType}
                  priorities={form.fieldPriorities}
                  onChange={(fieldPriorities) => patchForm({ fieldPriorities })}
                  onResetToDefault={handleResetToDefault}
                />
              </div>

              <div className="space-y-4">
                <DamagePromptPanel
                  categoryName={categoryName}
                  config={form}
                  onChange={patchForm}
                />
                <AspectPriorityList
                  partCategoryId={categoryId}
                  aspects={form.aspectPriorities ?? []}
                  aspectsFieldWeight={
                    form.fieldPriorities.find((p) => p.field === "Aspects")
                      ?.weight
                  }
                  onChange={(aspectPriorities) =>
                    patchForm({ aspectPriorities })
                  }
                />
                <TitleBuildingRules
                  removalOrder={form.removalOrder}
                  characterLimit={form.titleCharacterLimit}
                  onChange={(removalOrder) => patchForm({ removalOrder })}
                />
                <AdditionalInstructionsPanel
                  categoryName={categoryName}
                  config={form}
                  onChange={patchForm}
                />
              </div>

              <div className="space-y-4">
                <LiveTitlePreview
                  exampleData={exampleForPreview}
                  result={titlePreview}
                  isGenerating={previewTitle.isPending}
                  compiledPrompt={
                    titlePreview?.compiledSystemPrompt ??
                    config.data?.compiledSystemPrompt ??
                    null
                  }
                  onExampleDataChange={(patch) =>
                    setExampleData((current) => ({ ...current, ...patch }))
                  }
                  onGenerate={handlePreviewTitle}
                />
                <DamageDescriptionPreview
                  enabled={form.useCategoryDamagePrompt}
                  result={damagePreview}
                  isGenerating={previewDamage.isPending}
                  onGenerate={handlePreviewDamage}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <CategoryPickerModal
        open={addOpen}
        setOpen={setAddOpen}
        configured={false}
        title="Add categories"
        description="Each category you pick gets a default configuration for its type, ready to edit. Nothing changes for parts until you save it."
        emptyText="Every category already has a configuration."
        isSaving={create.isPending}
        confirmLabel={(count) =>
          `Add ${count} categor${count === 1 ? "y" : "ies"}`
        }
        onConfirm={handleAdd}
      />

      {categoryId && (
        <CategoryPickerModal
          open={duplicateOpen}
          setOpen={setDuplicateOpen}
          excludePartCategoryId={categoryId}
          warnOnConfigured
          title="Copy configuration"
          description={
            <>
              Copies the saved configuration for{" "}
              <span className="font-medium">{categoryName}</span> onto the
              categories you pick, adding them if they are not configured yet.
              Each prompt is recompiled with the destination category's own
              name.
            </>
          }
          emptyText="No other categories to copy onto."
          isSaving={duplicate.isPending}
          confirmLabel={(count) =>
            `Copy to ${count} categor${count === 1 ? "y" : "ies"}`
          }
          onConfirm={handleDuplicate}
        />
      )}

      <ConfirmModal
        open={!!pendingRemoval}
        setOpen={() => setPendingRemoval(undefined)}
        title={`Remove ${pendingRemoval?.name ?? ""}?`}
        message="Parts in this category will go back to the built-in title rules on the next category sync, and any damage prompt stops being used. You can add the category again later, but its settings will start from the defaults."
        confirmButtonText="Remove"
        isLoading={remove.isPending}
        onConfirm={handleRemove}
      />
    </div>
  );
}
